// ======================================================================
// File: src/modules/ipe/dashboard/crm/components/SalesPipelineWidget.jsx
// Purpose:
// CRM Executive Sales Funnel
// ======================================================================

import React from "react";

export default function SalesPipelineWidget({
  data = [],
  onViewDetails,
  onStageClick,
}) {
  const maxValue =
    Math.max(...data.map((x) => x.count)) || 1;

  const stageColors = [
    "#2563eb",
    "#7c3aed",
    "#f59e0b",
    "#10b981",
  ];

  const totalEnquiries =
    data?.[0]?.count || 0;

  const ordersWon =
    data?.[data.length - 1]?.count || 0;

  const overallConversion =
    (
      (ordersWon /
        (totalEnquiries || 1)) *
      100
    ).toFixed(1);

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "12px",
        padding: "18px",
        boxShadow:
          "0 2px 10px rgba(0,0,0,.06)",
        border: "1px solid #e5e7eb",
      }}
    >
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: 700,
          }}
        >
          Sales Pipeline
        </h3>

        <button
          onClick={onViewDetails}
          style={{
            border:
              "1px solid #d1d5db",
            background: "#fff",
            padding: "6px 12px",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 600,
          }}
        >
          View Details
        </button>
      </div>

      {/* =====================================================
          EXECUTIVE SUMMARY
      ===================================================== */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(3,1fr)",
          gap: "12px",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            background: "#eff6ff",
            borderRadius: "10px",
            padding: "12px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              color: "#64748b",
            }}
          >
            Pipeline Entries
          </div>

          <div
            style={{
              fontSize: "22px",
              fontWeight: 700,
              color: "#2563eb",
            }}
          >
            {totalEnquiries}
          </div>
        </div>

        <div
          style={{
            background: "#ecfdf5",
            borderRadius: "10px",
            padding: "12px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              color: "#64748b",
            }}
          >
            Orders Won
          </div>

          <div
            style={{
              fontSize: "22px",
              fontWeight: 700,
              color: "#10b981",
            }}
          >
            {ordersWon}
          </div>
        </div>

        <div
          style={{
            background: "#f5f3ff",
            borderRadius: "10px",
            padding: "12px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              color: "#64748b",
            }}
          >
            Win Rate
          </div>

          <div
            style={{
              fontSize: "22px",
              fontWeight: 700,
              color: "#7c3aed",
            }}
          >
            {overallConversion}%
          </div>
        </div>
      </div>

      {/* =====================================================
          FUNNEL
      ===================================================== */}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        {data.map((item, index) => {
          const width =
            (item.count / maxValue) *
            100;

          const conversion =
            index < data.length - 1
              ? (
                  (data[index + 1]
                    .count /
                    item.count) *
                  100
                ).toFixed(0)
              : null;

          return (
            <div
              key={item.stage}
              onClick={() =>
                onStageClick?.(item)
              }
              style={{
                cursor: "pointer",
                padding: "10px",
                borderRadius: "10px",
                transition:
                  "all .25s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "#f8fafc";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  "#fff";
              }}
            >
              {/* ROW */}

              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                  marginBottom:
                    "6px",
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    color:
                      "#334155",
                  }}
                >
                  {item.stage}
                </div>

                <div
                  style={{
                    display:
                      "flex",
                    gap: "8px",
                    alignItems:
                      "center",
                  }}
                >
                  {conversion && (
                    <span
                      style={{
                        background:
                          "#e0f2fe",
                        color:
                          "#0369a1",
                        fontSize:
                          "11px",
                        padding:
                          "2px 8px",
                        borderRadius:
                          "999px",
                        fontWeight:
                          600,
                      }}
                    >
                      {conversion}%
                    </span>
                  )}

                  <span
                    style={{
                      fontWeight:
                        700,
                      color:
                        stageColors[
                          index
                        ],
                    }}
                  >
                    {item.count}
                  </span>
                </div>
              </div>

              {/* BAR */}

              <div
                style={{
                  height: "16px",
                  background:
                    "#f1f5f9",
                  borderRadius:
                    "20px",
                  overflow:
                    "hidden",
                }}
              >
                <div
                  style={{
                    width: `${width}%`,
                    height:
                      "100%",
                    background:
                      stageColors[
                        index
                      ],
                    borderRadius:
                      "20px",
                    transition:
                      "width .4s ease",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* =====================================================
          FOOTER
      ===================================================== */}

      <div
        style={{
          marginTop: "18px",
          paddingTop: "14px",
          borderTop:
            "1px solid #e5e7eb",
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontSize: "12px",
            color: "#64748b",
          }}
        >
          Pipeline Health
        </div>

        <div
          style={{
            fontSize: "13px",
            fontWeight: 700,
            color: "#10b981",
          }}
        >
          Healthy Conversion Flow
        </div>
      </div>
    </div>
  );
}